/**
 * Certificate template API endpoints (Phase 15)
 */

import { buildQueryString, fetchApi, fetchRawApi } from '../core';
import { objectIdPath } from '../path';
import type {
  CertificateTemplateResponse,
  CertificateBatchResponse,
  CertificateEligibilityResponse,
  CreateCertificateBatchData,
  CertificateOperationsDiagnostics,
  CertificateRecoveryRequest,
  CertificateRecoveryResponse,
  CreateCertificateTemplateData,
  DownloadUrlResponse,
  IssueCertificateData,
  IssuedCertificateResponse,
  ListCertificatesParams,
  RevokeCertificateData,
  UpdateCertificateTemplateData,
} from '../types';

export function listTemplates(componentName: string = 'Unknown') {
  return fetchApi<CertificateTemplateResponse[]>('/certificate-templates', {}, componentName);
}

export function getTemplate(id: string, componentName: string = 'Unknown') {
  return fetchApi<CertificateTemplateResponse>(`/certificate-templates/${objectIdPath(id, 'certificate template')}`, {}, componentName);
}

export async function previewTemplatePdf(id: string, componentName: string = 'Unknown') {
  const response = await fetchRawApi(
    `/certificate-templates/${objectIdPath(id, 'certificate template')}/preview`,
    {},
    componentName
  );
  if (!response.ok) {
    throw new Error(`Certificate preview failed with status ${response.status}`);
  }
  return response.blob();
}

export function createTemplate(
  data: CreateCertificateTemplateData,
  componentName: string = 'Unknown'
) {
  return fetchApi<CertificateTemplateResponse>(
    '/certificate-templates',
    { method: 'POST', body: JSON.stringify(data) },
    componentName
  );
}

export function updateTemplate(
  id: string,
  data: UpdateCertificateTemplateData,
  componentName: string = 'Unknown'
) {
  return fetchApi<CertificateTemplateResponse>(
    `/certificate-templates/${objectIdPath(id, 'certificate template')}`,
    { method: 'PUT', body: JSON.stringify(data) },
    componentName
  );
}

export function deactivateTemplate(id: string, componentName: string = 'Unknown') {
  return fetchApi<CertificateTemplateResponse>(
    `/certificate-templates/${objectIdPath(id, 'certificate template')}`,
    { method: 'DELETE' },
    componentName
  );
}

// Issued certificates (Phase 15, Slice 5)

export function issueCertificate(data: IssueCertificateData, componentName: string = 'Unknown') {
  return fetchApi<IssuedCertificateResponse>(
    '/certificates',
    { method: 'POST', body: JSON.stringify(data) },
    componentName
  );
}

export function revokeCertificate(
  id: string,
  data: RevokeCertificateData,
  componentName: string = 'Unknown'
) {
  return fetchApi<IssuedCertificateResponse>(
    `/certificates/${objectIdPath(id, 'certificate')}/revoke`,
    { method: 'POST', body: JSON.stringify(data) },
    componentName
  );
}

export function listCertificates(
  params: ListCertificatesParams = {},
  componentName: string = 'Unknown'
) {
  return fetchApi<IssuedCertificateResponse[]>(
    `/certificates${buildQueryString(params as Record<string, string | undefined>)}`,
    {},
    componentName
  );
}

export function listMyCertificates(componentName: string = 'Unknown') {
  return fetchApi<IssuedCertificateResponse[]>('/certificates/mine', {}, componentName);
}

export function getCertificate(id: string, componentName: string = 'Unknown') {
  return fetchApi<IssuedCertificateResponse>(`/certificates/${objectIdPath(id, 'certificate')}`, {}, componentName);
}

/** Presigned URL for the certificate's institution logo (recipient or admin). */
export function getCertificateLogo(id: string, componentName: string = 'Unknown') {
  return fetchApi<DownloadUrlResponse>(`/certificates/${objectIdPath(id, 'certificate')}/logo`, {}, componentName);
}

export function renderCertificate(id: string, componentName: string = 'Unknown') {
  return fetchApi<IssuedCertificateResponse>(
    `/certificates/${objectIdPath(id, 'certificate')}/render`,
    { method: 'POST' },
    componentName
  );
}

export function deliverCertificate(id: string, componentName: string = 'Unknown') {
  return fetchApi<IssuedCertificateResponse>(
    `/certificates/${objectIdPath(id, 'certificate')}/deliver`,
    { method: 'POST' },
    componentName
  );
}

export function downloadCertificate(id: string, componentName: string = 'Unknown') {
  return fetchApi<DownloadUrlResponse>(`/certificates/${objectIdPath(id, 'certificate')}/download`, {}, componentName);
}

export function getCourseEligibility(courseId: string, componentName: string = 'Unknown') {
  return fetchApi<CertificateEligibilityResponse>(
    `/courses/${objectIdPath(courseId, 'course')}/certificate-eligibility`,
    {},
    componentName
  );
}

export function createBatch(data: CreateCertificateBatchData, componentName: string = 'Unknown') {
  return fetchApi<CertificateBatchResponse>(
    '/certificate-batches',
    { method: 'POST', body: JSON.stringify(data) },
    componentName
  );
}

export function listBatches(componentName: string = 'Unknown') {
  return fetchApi<CertificateBatchResponse[]>('/certificate-batches', {}, componentName);
}

export function getBatch(id: string, componentName: string = 'Unknown') {
  return fetchApi<CertificateBatchResponse>(`/certificate-batches/${objectIdPath(id, 'certificate batch')}`, {}, componentName);
}

export function retryBatch(id: string, componentName: string = 'Unknown') {
  return fetchApi<CertificateBatchResponse>(
    `/certificate-batches/${objectIdPath(id, 'certificate batch')}/retry`,
    { method: 'POST' },
    componentName
  );
}

export function getDiagnostics(componentName: string = 'Unknown') {
  return fetchApi<CertificateOperationsDiagnostics>(
    '/certificate-operations/diagnostics',
    {},
    componentName
  );
}

export function recoverCertificates(
  data: CertificateRecoveryRequest,
  componentName: string = 'Unknown'
) {
  return fetchApi<CertificateRecoveryResponse>(
    '/certificate-operations/recover',
    { method: 'POST', body: JSON.stringify(data) },
    componentName
  );
}
